import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import { Maybe } from '@Types'
import ModalPortal from '@Components/Popups/ModalPortal'
import { StoresContext } from '@App/contexts'
import { PaginationInfo } from '@Entities/pagination'
import { Navigate } from 'react-router'
import FileReviewStepPopup from '@Scenes/WorkScenes/Steps/FileReviewStep/FileReviewStepPopup'
import FileReviewErrors from '@Scenes/WorkScenes/Steps/FileReviewStep/FileReviewErrors'
import FileReviewCompletedWorks from '@Scenes/WorkScenes/Steps/FileReviewStep/FileReviewCompletedWorks'
import RootStore from '@App/stores/root-store'
import { FileReviewExpandWorks } from '@Scenes/WorkScenes/Steps/FileReviewStep/file-review-expand-works'

const limitItems = 10

const FileReviewStep = () => {
    const { bulkUploadStore, workStore, rightsholderStore, uiStore } = useContext<RootStore>(StoresContext)

    const [reviewCount, setReviewCount] = useState<number>()
    const [currentPaginationPage, setCurrentPaginationPage] = useState<Maybe<PaginationInfo>>(null)
    const [startTitlePosition, setStartTitlePosition] = useState<number>()
    const [endTitlePosition, setEndTitlePosition] = useState<number>()
    const [currentNumPage, setCurrentNumPage] = useState<number>()
    const [readyWorksExpanded, setReadyWorksExpanded] = useState<boolean>(false)
    const [errorWorksExpanded, setErrorWorksExpanded] = useState<boolean>(true)

    const setTitlePositions = () => {
        const arrayWorks = bulkUploadStore.worksFromUpload
        const startPosition = bulkUploadStore.startTitlePositionWorkSubmissions

        if (!arrayWorks || !currentPaginationPage) {
            return
        }

        if (currentNumPage && currentNumPage === currentPaginationPage.currentPage) {
            return
        }

        if (!startTitlePosition || !endTitlePosition) {
            return
        }

        const startOld = startTitlePosition
        const endOld = endTitlePosition
        const oldNumPage = currentNumPage
        const countWorks = arrayWorks.error.length + arrayWorks.complete.length

        let startNew
        let endNew

        if (oldNumPage) {
            if (currentPaginationPage.currentPage > oldNumPage) {
                startNew = endOld + 1
                endNew = startNew + countWorks - 1
            } else {
                endNew = startOld - 1
                startNew = endNew - countWorks + 1
            }
        } else {
            // for the first call
            // if start position is saved in the store
            startNew = startPosition ? startPosition : 1
            endNew = startPosition ? startPosition + countWorks - 1 : countWorks
        }

        setStartTitlePosition(startNew)
        setEndTitlePosition(endNew)
        setCurrentNumPage(currentPaginationPage.currentPage)

        bulkUploadStore.setStartTitlePositionWorkSubmissions(startNew)
    }

    const setCurrentPaginationPageFromBulkStore = () => {
        const paginationErrorWorks = bulkUploadStore.worksCompleteFromUploadPaginationInfo
        const paginationCompleteWorks = bulkUploadStore.worksCompleteFromUploadPaginationInfo

        const arrayWorks = bulkUploadStore.worksFromUpload

        if (!arrayWorks) {
            return
        }

        if (arrayWorks.completeCount <= limitItems && arrayWorks.errorCount <= limitItems) {
            setCurrentPaginationPage(null)
        } else {
            setCurrentPaginationPage(
                arrayWorks.completeCount > arrayWorks.errorCount ? paginationCompleteWorks : paginationErrorWorks
            )
        }
    }

    const setReviewCountFromBulkStore = () => {
        const arrayWorks = bulkUploadStore.worksFromUpload
        if (!arrayWorks) {
            return
        }
        setReviewCount(arrayWorks.completeCount + arrayWorks.errorCount)
    }

    const expandSection = (section: FileReviewExpandWorks) => {
        switch (section) {
            case FileReviewExpandWorks.ready:
                setReadyWorksExpanded(true)
                break
            case FileReviewExpandWorks.error:
                setErrorWorksExpanded(true)
                break
        }
    }

    const contractSection = (section: FileReviewExpandWorks) => {
        switch (section) {
            case FileReviewExpandWorks.ready:
                setReadyWorksExpanded(false)
                break
            case FileReviewExpandWorks.error:
                setErrorWorksExpanded(false)
                break
        }
    }

    useEffect(() => {
        setTitlePositions()
    })

    useEffect(() => {
        setCurrentPaginationPageFromBulkStore()
        setReviewCountFromBulkStore()
    }, [])

    const workDraftFromLocal = workStore.workDraftLocal
    const currentRightsHolderId = rightsholderStore.currentRightsholderId

    if (!workDraftFromLocal) {
        return null
    }

    const arrayWorks = bulkUploadStore.worksFromUpload
    const allArrayWorks = bulkUploadStore.allWorksFromUpload

    if (!arrayWorks || !allArrayWorks) {
        return <Navigate to={`/member/${currentRightsHolderId}/create-work/bulk`} />
    }

    const numComplete = allArrayWorks.complete.length
    const numErrors = allArrayWorks.error.length
    const openModal = uiStore.bulkUploadModalIsOpen

    return (
        <>
            <div className="head-create-work-wizard head-of-bulk">
                <div>
                    <h2>
                        Create Work: <span>File Review</span>
                    </h2>
                </div>
                <div className="counter-wrapper">
                    <p className="invalid-count">You have ({numErrors}) works with an error</p>
                    <p className="valid-count">You have ({numComplete}) works without an error</p>
                </div>
            </div>
            <div className="file-review-step">
                <div className="search-review-wrapper">
                    <p className="small bold">Showing {reviewCount} files for review</p>
                </div>
                <div className="file-review-wrapper">
                    <div className="works-list-wrapper">
                        {arrayWorks.complete.length > 0 && (
                            <FileReviewCompletedWorks
                                readyWorksExpanded={readyWorksExpanded}
                                expandSection={expandSection}
                                contractSection={contractSection}
                                numComplete={numComplete}
                                completedWorks={arrayWorks.complete}
                            />
                        )}
                        {arrayWorks.error.length > 0 && (
                            <FileReviewErrors
                                numErrors={numErrors}
                                errorWorksExpanded={errorWorksExpanded}
                                expandSection={expandSection}
                                contractSection={contractSection}
                                worksWithError={arrayWorks.error}
                            />
                        )}
                    </div>
                    <ModalPortal isOpen={openModal} onClose={() => uiStore.setBulkUploadModalIsOpen(false)}>
                        <FileReviewStepPopup numComplete={numComplete} numErrors={numErrors} />
                    </ModalPortal>
                </div>
            </div>
        </>
    )
}

export default observer(FileReviewStep)
